import { useEffect, useMemo, useRef, useState } from 'react'
import type { AlbumConfig, StickerInvestment, ThemeDefinition } from '../types'
import { stickerTitle, stickerImage } from '../data'
import { useI18n } from '../i18n/context'

interface Props {
  stickerNumbers: number[]
  stickers: Record<number, StickerInvestment>
  theme: ThemeDefinition
  album: AlbumConfig
  stickersPerPage: number
  pageSizeOptions: readonly number[]
  onPageSizeChange: (pageSize: number) => void
  onStickerClick: (stickerNumber: number) => void
}

interface StickerTileProps {
  stickerNumber: number
  index: number
  investment?: StickerInvestment
  imageUrl: string | null
  title: string
  isCollected: boolean
  onClick: (stickerNumber: number) => void
  onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => void
  label: string
}

function StickerTile({
  stickerNumber,
  index,
  investment,
  imageUrl,
  title,
  isCollected,
  onClick,
  onKeyDown,
  label
}: StickerTileProps): JSX.Element {
  const [hasImageError, setHasImageError] = useState(false)

  useEffect(() => {
    setHasImageError(false)
  }, [imageUrl])

  const shouldShowImage = isCollected && !!imageUrl && !hasImageError

  return (
    <button
      className={isCollected ? 'sticker is-collected' : 'sticker'}
      onClick={() => onClick(stickerNumber)}
      onKeyDown={(event) => onKeyDown(event, index)}
      type="button"
      role="gridcell"
      aria-label={label}
      aria-pressed={isCollected}
      tabIndex={index === 0 ? 0 : -1}
    >
      {isCollected ? (
        <div className="sticker-collected-sheet" aria-hidden="true">
          {shouldShowImage ? (
            <img
              className="sticker-sheet-art"
              src={imageUrl}
              alt=""
              loading="lazy"
              onError={() => setHasImageError(true)}
            />
          ) : (
            <span className="sticker-sheet-fallback">{stickerNumber}</span>
          )}
          <span className="sticker-sheet-name">{title}</span>
        </div>
      ) : (
        <span className="sticker-centered-number" aria-hidden="true">
          {stickerNumber}
        </span>
      )}

      <span className="sr-only">{title}</span>
      {investment ? <span className="sr-only">Collected</span> : null}
    </button>
  )
}

export function StickerGrid({
  stickerNumbers,
  stickers,
  theme,
  album,
  stickersPerPage,
  pageSizeOptions,
  onPageSizeChange,
  onStickerClick
}: Props): JSX.Element {
  const { t } = useI18n()
  const gridRef = useRef<HTMLDivElement>(null)
  const [currentPage, setCurrentPage] = useState(0)

  const pages = useMemo(() => {
    const result: number[][] = []
    for (let index = 0; index < stickerNumbers.length; index += stickersPerPage) {
      result.push(stickerNumbers.slice(index, index + stickersPerPage))
    }
    return result
  }, [stickerNumbers, stickersPerPage])

  useEffect(() => {
    setCurrentPage(0)
  }, [album.id, stickerNumbers.length])

  const totalPages = pages.length
  const page = pages[currentPage] ?? []

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number): void {
    const buttons = gridRef.current?.querySelectorAll<HTMLButtonElement>('button.sticker')
    if (!buttons) return
    let next = -1
    if (e.key === 'ArrowRight') next = index + 1
    else if (e.key === 'ArrowLeft') next = index - 1
    else if (e.key === 'ArrowDown') {
      const cols = Array.from(buttons).filter((b) => b.offsetTop === buttons[0].offsetTop).length
      next = index + cols
    } else if (e.key === 'ArrowUp') {
      const cols = Array.from(buttons).filter((b) => b.offsetTop === buttons[0].offsetTop).length
      next = index - cols
    }
    if (next >= 0 && next < buttons.length) {
      e.preventDefault()
      buttons[next].focus()
    }
  }

  return (
    <div className="sticker-grid-pager">
      <div
        className="sticker-grid"
        ref={gridRef}
        role="grid"
        aria-label={`${album.name} sticker grid`}
      >
        {page.map((stickerNumber, index) => {
          const investment = stickers[stickerNumber]
          const imageUrl = stickerImage(theme, stickerNumber)
          const title = stickerTitle(theme, stickerNumber)
          const label = investment
            ? `${t.collected}: #${stickerNumber} ${title}`
            : `${t.openSlot}: #${stickerNumber} ${title}`

          return (
            <StickerTile
              key={stickerNumber}
              stickerNumber={stickerNumber}
              index={index}
              investment={investment}
              imageUrl={imageUrl}
              title={title}
              isCollected={!!investment}
              onClick={onStickerClick}
              onKeyDown={handleKeyDown}
              label={label}
            />
          )
        })}

        {page.length < stickersPerPage &&
          Array.from({ length: stickersPerPage - page.length }).map((_, index) => (
            <div key={`sticker-empty-${index}`} className="sticker is-empty" aria-hidden="true" />
          ))}
      </div>
      <div className="sticker-grid-footer">
        <div className="sticker-grid-footer-spacer" aria-hidden="true" />
        <div className="sticker-grid-controls">
          <button
            className="ghost-button flipbook-arrow"
            type="button"
            onClick={() => setCurrentPage((page) => Math.max(page - 1, 0))}
            disabled={currentPage === 0}
            aria-label={t.previousPage}
          >
            ‹
          </button>
          <span className="flipbook-page-indicator">
            <span>{t.pageOf(currentPage + 1, totalPages)}</span>
            <small>
              {t.stickersRange(
                currentPage * stickersPerPage + 1,
                Math.min((currentPage + 1) * stickersPerPage, stickerNumbers.length)
              )}
            </small>
          </span>
          <button
            className="ghost-button flipbook-arrow"
            type="button"
            onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages - 1))}
            disabled={currentPage === totalPages - 1}
            aria-label={t.nextPage}
          >
            ›
          </button>
        </div>
        <label className="sticker-page-size-control">
          <span>{t.albumPageSize}</span>
          <select
            className="settings-select sticker-page-size-select"
            value={stickersPerPage}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            aria-label={t.albumPageSize}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {t.albumPageSizeValue(option)}
              </option>
            ))}
          </select>
        </label>
      </div>
    </div>
  )
}
